import { PlusIcon, TrashIcon, ViewfinderCircleIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { FormFieldDefinition } from "../../db/schema";
import Button from "../common/Button";

// Properties-panel editor for a "circle" field: the printed words to choose
// from, whether several can be circled, and marking where each word sits on the
// PDF (the filler draws an ellipse there).
interface Props {
  field: FormFieldDefinition;
  placing: { fieldId: string; index: number } | null;
  onChange: (updates: Partial<FormFieldDefinition>) => void;
  // Start marking: `single` places just that option, otherwise it carries on to the next.
  onPlace: (index: number, single?: boolean) => void;
  onStopPlacing: () => void;
}

export default function CircleOptionsEditor({ field, placing, onChange, onPlace, onStopPlacing }: Props) {
  const options = field.options || [];
  const rects = field.optionRects || [];
  const placedCount = options.filter((_, i) => rects[i]).length;
  const activeIndex = placing?.fieldId === field.id ? placing.index : null;

  const setOption = (i: number, text: string) => onChange({ options: options.map((o, k) => (k === i ? text : o)) });
  const removeOption = (i: number) =>
    onChange({
      options: options.filter((_, k) => k !== i),
      optionRects: rects.filter((_, k) => k !== i),
    });
  const addOption = () => onChange({ options: [...options, ""] });

  return (
    <div className="form-control space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm">Options</span>
        <span className="text-xs text-base-content/60">
          {placedCount} of {options.length} marked on the form
        </span>
      </div>

      <ul className="m-0 p-0 list-none space-y-1">
        {options.map((opt, i) => (
          <li key={i} className="flex items-center gap-1">
            <input
              className={cn(
                "flex-1 min-w-0 h-8 px-2 text-sm border rounded-md bg-base-100 focus:outline-none",
                activeIndex === i ? "border-amber-500" : "border-base-300 focus:border-gray-400 dark:focus:border-base-content/30",
              )}
              value={opt}
              placeholder="Printed word"
              onChange={(e) => setOption(i, e.target.value)}
            />
            <button
              type="button"
              title={rects[i] ? "Mark this word again on the form" : "Mark this word on the form"}
              className={cn(
                "p-1 rounded hover:bg-base-200 transition-colors",
                rects[i] ? "text-blue-600 dark:text-blue-300" : "text-amber-600 dark:text-amber-400",
              )}
              onClick={() => onPlace(i, true)}
            >
              <ViewfinderCircleIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Remove option"
              className="p-1 rounded text-base-content/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
              onClick={() => removeOption(i)}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-2">
        <Button variant="secondary" size="xs" onClick={addOption}>
          <PlusIcon className="h-3.5 w-3.5 mr-1" />
          Add option
        </Button>
        {options.length > 0 &&
          (activeIndex !== null ? (
            <Button variant="outline" size="xs" onClick={onStopPlacing}>
              Stop marking
            </Button>
          ) : (
            <Button variant="secondary" size="xs" onClick={() => onPlace(Math.max(0, options.findIndex((_, i) => !rects[i])))}>
              Mark on form
            </Button>
          ))}
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500"
          checked={!!field.multiple}
          onChange={(e) => onChange({ multiple: e.target.checked })}
        />
        <span className="text-sm">Allow circling more than one</span>
      </label>
      <p className="text-xs text-base-content/50 m-0">
        {field.multiple ? "Circle all that apply: the filler can circle several options." : "Circle one: the filler circles a single option."}
      </p>
    </div>
  );
}
